// Develop — dev-only diagnosticka sekce (jen kdyz backend bezi v Development + pro ADMIN role).
//
// Viditelnost: nav polozka v Layout.tsx je `devOnly` + `adminOnly`. "Dev" se urcuje
// z backendu (AuthContext.isDev <- GET /api/health env), takze sekce je videt i v
// Docker dev stacku, kde je web produkcni build. Tato stranka guarduje obe podminky
// znovu, aby primy prechod na /develop nic neukazal mimo dev/admin.
//
// Backend: POST /api/notifikace/test-email je dostupny jen v Development prostredi
// ASP.NET (jinak 404) a vyzaduje admin policy.

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { FlaskConical, Loader2, Lock, Mail, Send } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import { api, ApiError } from '@/lib/api';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface TestEmailResult {
  ok: boolean;
  to: string;
  host: string;
  port: number;
  useTls: boolean;
  fromAddress: string;
}

interface DispatchResult {
  processed: number;
  sent: number;
  failed: number;
}

export function DevelopPage() {
  const { isAdmin, isDev } = useAuth();

  // Mimo Development prostredi backendu sekce neexistuje — primy prechod na /develop skoncí tady.
  if (!isDev) {
    return <NotAvailable />;
  }

  if (!isAdmin) {
    return (
      <div className="flex items-start">
        <Card className="max-w-md">
          <CardHeader>
            <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-md bg-destructive/10 text-destructive">
              <Lock className="h-5 w-5" />
            </div>
            <CardTitle>Nemáte oprávnění</CardTitle>
            <CardDescription>
              Sekce Develop je dostupná pouze pro roli <strong>ADMIN</strong>.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return <DevelopView />;
}

function NotAvailable() {
  return (
    <div className="flex items-start">
      <Card className="max-w-md">
        <CardHeader>
          <CardTitle>Nedostupné</CardTitle>
          <CardDescription>Sekce Develop je dostupná jen ve vývojovém režimu.</CardDescription>
        </CardHeader>
      </Card>
    </div>
  );
}

function DevelopView() {
  const { toast } = useToast();
  const [to, setTo] = useState('');
  const [lastResult, setLastResult] = useState<TestEmailResult | null>(null);

  const testEmail = useMutation({
    mutationFn: (recipient: string) =>
      api<TestEmailResult>('/notifikace/test-email', {
        method: 'POST',
        body: JSON.stringify({ to: recipient }),
      }),
    onSuccess: (res) => {
      setLastResult(res);
      toast({
        title: 'E-mail odeslán',
        description: `Testovací zpráva odešla na ${res.to} přes ${res.host}:${res.port}.`,
      });
    },
    onError: (err: ApiError) => {
      setLastResult(null);
      toast({ title: 'Odeslání selhalo', description: err.message, variant: 'destructive' });
    },
  });

  const flushNow = useMutation({
    mutationFn: () => api<DispatchResult>('/notifikace/flush-now', { method: 'POST' }),
    onSuccess: (res) => {
      toast({
        title: 'Dispatcher spuštěn',
        description: `Zpracováno ${res.processed}, odesláno ${res.sent}, selhalo ${res.failed}.`,
      });
    },
    onError: (err: ApiError) =>
      toast({ title: 'Spuštění selhalo', description: err.message, variant: 'destructive' }),
  });

  const recipient = to.trim();
  const canSend = recipient.length > 0 && !testEmail.isPending;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (canSend) testEmail.mutate(recipient);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-amber-100 text-amber-700">
          <FlaskConical className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-xl font-semibold">Develop</h1>
          <p className="text-sm text-muted-foreground">
            Vývojové nástroje — dostupné jen v dev režimu pro administrátora.
          </p>
        </div>
      </div>

      {/* Test e-mailu */}
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-4 w-4" /> Test e-mailu
          </CardTitle>
          <CardDescription>
            Odešle jeden testovací e-mail přímo přes SMTP (mimo frontu notifikací) pro ověření
            konfigurace <code>Smtp__*</code>.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="test-email-to">Komu (e-mail)</Label>
              <Input
                id="test-email-to"
                type="email"
                placeholder="jan.svoboda@dpb.cz"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                autoComplete="off"
              />
            </div>
            <Button type="submit" disabled={!canSend} className="gap-2">
              {testEmail.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Odeslat testovací e-mail
            </Button>
          </form>

          {lastResult && (
            <div className="mt-4 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
              ✅ Odesláno na <strong>{lastResult.to}</strong> přes{' '}
              <code>
                {lastResult.host}:{lastResult.port}
              </code>{' '}
              ({lastResult.useTls ? 'StartTLS' : 'Auto'}), od {lastResult.fromAddress}.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Flush fronty notifikaci */}
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="h-4 w-4" /> Spustit dispatcher notifikací
          </CardTitle>
          <CardDescription>
            Vynutí jeden dispatch cyklus fronty <code>notifikace_queue</code> hned (bez čekání na
            30s tick BackgroundService).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            variant="outline"
            disabled={flushNow.isPending}
            onClick={() => flushNow.mutate()}
            className="gap-2"
          >
            {flushNow.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Flush now
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
